import { queryOptions } from "@tanstack/react-query"
import { api } from "./api"
import { compareWallClockToNow, differenceWallClockDays } from "./local-datetime"
import {
  eventTravelKey,
  formatDateTime,
  type EventTravelResponse,
  type RoomEntry,
} from "./travel"

// Lodging / accommodation data layer for the Console. Reuses the admin
// /api/v1/admin/event-travel read shape (the `rooms` array) — the same endpoint
// TravelPage uses — and flattens reservations into bookable rows for the
// CollectionView + a reading-pane detail. Read-only v1.

export const lodgingKey = (eventId: string) => eventTravelKey(eventId)

// Share the travel cache key so lodging + travel hit one round-trip.
export function lodgingQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: lodgingKey(eventId),
    queryFn: () =>
      api<EventTravelResponse>("/api/v1/admin/event-travel", {
        method: "POST",
        body: { eventId },
      }),
  })
}

export type LodgingOccupant = {
  id: string
  name: string
  email?: string | null
}

export type LodgingStatus = "upcoming" | "checked_in" | "checked_out"

export type LodgingDetailRow = { key: string; label: string; value: string }

export type LodgingBooking = {
  id: string
  hotelName: string
  location: string | null
  checkInAt: string | null
  checkOutAt: string | null
  nights: number | null
  roomType: string | null
  bedConfiguration: string | null
  occupancyLabel: string | null
  confirmation: string | null
  status: LodgingStatus
  occupants: LodgingOccupant[]
  detailRows: LodgingDetailRow[]
}

export function buildLodging(data: EventTravelResponse | undefined): LodgingBooking[] {
  if (!data?.success) return []
  return (data.rooms ?? []).map(toBooking).sort(compareLodging)
}

function toBooking(entry: RoomEntry): LodgingBooking {
  const r = entry.reservation
  const nights = nightsBetween(r.checkInAt, r.checkOutAt)
  const location = [r.addressLocality, r.addressRegion].filter(Boolean).join(", ") || null
  const occupancyLabel = occupancy(r.numAdults, r.numChildren)
  return {
    id: r.id,
    hotelName: r.hotelName,
    location,
    checkInAt: r.checkInAt,
    checkOutAt: r.checkOutAt,
    nights,
    roomType: r.roomType,
    bedConfiguration: r.bedConfiguration,
    occupancyLabel,
    confirmation: r.confirmationNumber,
    status: deriveStatus(r.checkInAt, r.checkOutAt),
    occupants: entry.occupants.map((o) => ({
      id: o.guest.id,
      name: o.guest.name,
      email: o.guest.email,
    })),
    detailRows: [
      ["Hotel", r.hotelName],
      ["Location", location],
      ["Room type", r.roomType],
      ["Bed", r.bedConfiguration],
      ["Occupancy", occupancyLabel],
      ["Confirmation", r.confirmationNumber],
      nights != null ? ["Nights", String(nights)] : null,
    ]
      .filter((row): row is [string, string] => Array.isArray(row) && !!row[1])
      .map(([key, value]) => ({ key, label: key, value })),
  }
}

export function compareLodging(a: LodgingBooking, b: LodgingBooking): number {
  const t = (a.checkInAt ?? "9999").localeCompare(b.checkInAt ?? "9999")
  if (t !== 0) return t
  return a.hotelName.localeCompare(b.hotelName)
}

export function occupantsLabel(occupants: LodgingOccupant[]): string {
  if (occupants.length === 0) return "No guests"
  if (occupants.length === 1) return occupants[0].name
  return `${occupants[0].name} +${occupants.length - 1}`
}

const STATUS_LABEL: Record<LodgingStatus, string> = {
  upcoming: "Upcoming",
  checked_in: "Checked-in",
  checked_out: "Checked-out",
}
export function lodgingStatusLabel(status: LodgingStatus): string {
  return STATUS_LABEL[status]
}

// Status from check-in / check-out windows (no server-side stay status yet).
function deriveStatus(checkInAt: string | null, checkOutAt: string | null): LodgingStatus {
  const checkOut = checkOutAt ? compareWallClockToNow(checkOutAt) : null
  const checkIn = checkInAt ? compareWallClockToNow(checkInAt) : null
  if (checkOut != null && checkOut < 0) return "checked_out"
  if (checkIn != null && checkIn <= 0) return "checked_in"
  return "upcoming"
}

function nightsBetween(a: string | null, b: string | null): number | null {
  if (!a || !b) return null
  const nights = differenceWallClockDays(a, b)
  return nights == null ? null : Math.max(0, nights)
}

function occupancy(adults: number | null, children: number | null): string | null {
  const parts: string[] = []
  if (adults != null && adults > 0) parts.push(`${adults} adult${adults === 1 ? "" : "s"}`)
  if (children != null && children > 0) parts.push(`${children} child${children === 1 ? "" : "ren"}`)
  return parts.length ? parts.join(", ") : null
}

// Re-export the shared date formatter so the lodging surface doesn't reach back
// into core/travel for it.
export { formatDateTime }
